import React from 'react'
import ReactDOM from 'react-dom'
import { QueryClient, QueryClientProvider } from 'react-query'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { auth } from './config/firebase.config'

// Initialize page load performance logger (dev only, auto-detects localhost)
// VNG-722: Updated to include PostHog and FCM environment variables
import './utils/performance-tracker'

// Register service worker for FCM push notifications
if ('serviceWorker' in navigator) {
  navigator.serviceWorker
    .register('/firebase-messaging-sw.js')
    .then((registration) => {
      // If SW is waiting to take control, tell it to do so immediately
      if (registration.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' })
      }

      if (navigator.serviceWorker.controller) {
        window.serviceWorkerReady = true
      } else {
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          window.serviceWorkerReady = true
        })
      }
    })
    .catch((error) => {
      console.error('Service worker registration failed:', error)
    })
}

import KbRedirect from '_pages/kb-redirect/kb-redirect.component'
import { isArticlesHost } from '_utils/brand-hint.util'

import './styles/global.css'

import '@hapana/shared/web/styles/fonts.css'
import '@hapana/shared/web/styles/reset.css'
import { DialogProvider } from '@hapana/shared/web/components/shared/dialog/dialog.provider'
import DndProviderWrapper from '@hapana/shared/web/components/shared/dnd-provider/dnd-provider.component'
import { ToastProvider } from '@hapana/shared/web/components/shared/toast/toast.provider'
import { PDFGenerationProvider } from '@hapana/shared/web/contexts'
import ThemeProvider from '@hapana/shared/web/components/bootstrap/theme-provider/theme-provider.component'
import ReactErrorBoundary from '@hapana/shared/web/components/bootstrap/error-boundary/react-error-boundary.component'
import ErrorBoundaryFallback from '@hapana/shared/web/components/bootstrap/error-boundary/error-boundary-fallback.jsx'
import { LayoutSkeleton } from './components/page-skeletons'
import { hasStoredCoreSession } from './utils/stored-session.util'

import App from './app.component.jsx'
import { initPostHog } from './services/posthog'
import { initMetricsTracking } from './services/metrics'
import { trackEnhancedError } from './services/enhanced-error-tracking'
import { setGQtyErrorReporter } from '@hapana/shared/web/services/gqty'
import { applyEnvironmentFavicon } from '@hapana/shared/web/utils/environment'
import { useAuthInitialize } from './hooks/use-auth-initialize'
import BrandNotFound from './pages/brand-not-found/brand-not-found.component'
import { useAppTheme } from './components/theme-manager/theme-manager.component'
import { CoreAuditTrackerProvider } from './providers/audit-tracker.provider'

applyEnvironmentFavicon()

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Don't retry on server errors (5xx) or client errors (4xx)
        // Only retry on network errors (connection issues, timeouts)
        if (error?.response?.status >= 400) {
          return false
        }
        // Don't retry at all for GraphQL errors
        if (error?.extensions?.code === 'INTERNAL_SERVER_ERROR') {
          return false
        }
        return 0 // No retries for any other errors
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      // Prevent automatic refetching on mount if query has failed
      refetchOnMount: (query) => {
        // Don't refetch if the query has failed with a server error
        if (query.state.error?.response?.status >= 500) {
          return false
        }
        return true
      },
    },
  },
})

initPostHog({
  apiKey: import.meta.env.VITE_POSTHOG_KEY || '',
  apiHost: import.meta.env.VITE_POSTHOG_HOST,
  enableHeatmaps: true,
  enableSessionRecording: true,
  enableErrorTracking: true,
})

// Wire GQty errors into the same error reporting pipeline as backend errors.
// Every query/mutation failure flows through queryFetcher → this reporter →
// trackEnhancedError → PostHog + /v2/fe-errors → Slack.
// Silent errors (401, not found, etc.) are already filtered inside queryFetcher.
setGQtyErrorReporter((error, { operation, code }) => {
  trackEnhancedError(error, {
    componentName: operation,
    feature: 'gqty',
    metadata: { gqty_error_code: code },
  })
})

initMetricsTracking()

// Check if we're on the demo subdomain (production: demo.*, dev: localhost with demo paths)
const isDemoSubdomain = () => {
  const hostname = window.location.hostname
  if (hostname.split('.')[0] === 'demo') {
    return true
  }
  // In development, treat as demo when VITE_DEMO_STAFF_TENANT_ID is set (demo infra configured)
  if (
    (hostname === 'localhost' || hostname === '127.0.0.1') &&
    import.meta.env.VITE_DEMO_STAFF_TENANT_ID
  ) {
    return true
  }
  return false
}

// Demo public paths — these routes bypass auth initialization entirely.
// They work on demo.* subdomain in production AND on localhost in development.
const DEMO_PUBLIC_PATHS = ['/signup', '/verify', '/creating', '/magic-link']

const isDemoPublicPath = () => {
  const path = window.location.pathname
  const isMatch = DEMO_PUBLIC_PATHS.some((p) => path === p || path.startsWith(p + '/'))
  if (!isMatch) {
    return false
  }
  // On demo subdomain or localhost — demo paths are always public
  return isDemoSubdomain()
}

// Check if we should show the navigation skeleton (not on login or demo public pages)

// Check if current path is a public route that doesn't require auth
const isPublicRoute = () => {
  const path = window.location.pathname
  if (path.startsWith('/confirm/')) {
    return true
  }
  if (isDemoPublicPath()) {
    return true
  }
  // /login is always public — it manages its own auth state and must never be blocked by
  // AppWithAuth's tenant/auth loading checks (which would hide the form during sign-in)
  if (path === '/login' || path.startsWith('/login/')) {
    return true
  }
  // /magic-link and /reset-password are always public — Firebase redirects here without brand
  // context for both flows. The callback/page components handle their own tenant redirect.
  if (path === '/magic-link' || path.startsWith('/magic-link/')) {
    return true
  }
  if (path === '/reset-password' || path.startsWith('/reset-password/')) {
    return true
  }
  return false
}

function AppWithAuth() {
  const { isLoading: tenantLoading, error: tenantError, isBrandNotFound } = useAuthInitialize()

  // On demo subdomain, redirect unauthenticated users on non-public paths to /signup.
  // Authenticated demo users (who completed the /signup → /verify → /creating flow) can access the dashboard.
  // We check both auth.currentUser (available after Firebase resolves) and the demo-authenticated
  // sessionStorage flag (set during verify, available immediately on page load before Firebase resolves).
  if (isDemoSubdomain() && !isDemoPublicPath()) {
    const path = window.location.pathname
    const hasFirebaseUser = auth.currentUser != null
    const hasDemoSession = sessionStorage.getItem('demo-authenticated') === 'true'
    // Allow /login for demo auth flow, and allow authenticated users to access all routes
    if (!hasFirebaseUser && !hasDemoSession && path !== '/login' && !path.startsWith('/login')) {
      console.log('[AppWithAuth] Demo subdomain, no auth session, redirecting to /signup', { path })
      window.location.replace('/signup')
      return null
    }
  }

  const _isPublic = isPublicRoute()
  const _path = window.location.pathname
  const _hostname = window.location.hostname

  // Log routing decisions for demo debugging
  if (_path.startsWith('/creating') || _path.startsWith('/verify') || _path.startsWith('/signup')) {
    console.log('[AppWithAuth] Routing decision:', {
      path: _path,
      hostname: _hostname,
      isPublicRoute: _isPublic,
      isDemoSubdomain: isDemoSubdomain(),
      isDemoPublicPath: isDemoPublicPath(),
      tenantLoading,
      tenantError: tenantError?.message || tenantError,
      isBrandNotFound,
      hasDemoTenantEnv: !!import.meta.env.VITE_DEMO_STAFF_TENANT_ID,
    })
  }

  // Skip auth checks for public routes
  if (_isPublic) {
    return (
      <ToastProvider>
        <DialogProvider>
          <App />
        </DialogProvider>
      </ToastProvider>
    )
  }

  if (isBrandNotFound) {
    console.log('[AppWithAuth] Brand not found — showing BrandNotFound page')
    return <BrandNotFound />
  }

  if (tenantLoading) {
    // A returning signed-in user gets the app shell at once; anyone else still sees nothing, so the
    // login page never flashes the authenticated layout (INT-1539).
    return hasStoredCoreSession() ? <LayoutSkeleton /> : null
  }

  if (tenantError) {
    const currentPath = window.location.pathname
    const isOnLoginPage = currentPath === '/login' || currentPath.includes('/login')

    console.log('[AppWithAuth] Tenant error detected:', {
      error: tenantError,
      currentPath,
      isOnLoginPage,
    })

    if (!isOnLoginPage) {
      console.log('[AppWithAuth] ⚠️ Redirecting to /login due to tenant error!')
      try {
        localStorage.clear()
        sessionStorage.clear()
      } catch (_e) {
        // Ignore storage errors
      }
      const currentSearch = window.location.search
      const loginUrl = `/login${currentSearch}`
      window.location.replace(loginUrl)

      // Show skeleton while redirecting
      return <LayoutSkeleton showNav={false} />
    }
  }

  return (
    <ToastProvider>
      <DialogProvider>
        <App />
        <ToastContainer />
      </DialogProvider>
    </ToastProvider>
  )
}

function RootWithTheme() {
  const theme = useAppTheme()

  // articles.<root> is a public redirector: it must never mount the app router, so no
  // authenticated surface or article content can render on an unauthenticated host.
  if (isArticlesHost()) {
    return (
      <ThemeProvider theme={theme}>
        <QueryClientProvider client={queryClient}>
          <KbRedirect />
        </QueryClientProvider>
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider theme={theme}>
      <QueryClientProvider client={queryClient}>
        <PDFGenerationProvider>
          <DndProviderWrapper>
            <CoreAuditTrackerProvider>
              <AppWithAuth />
            </CoreAuditTrackerProvider>
          </DndProviderWrapper>
        </PDFGenerationProvider>
      </QueryClientProvider>
    </ThemeProvider>
  )
}

function Root() {
  return (
    <React.StrictMode>
      <ReactErrorBoundary fallback={<ErrorBoundaryFallback />}>
        <RootWithTheme />
      </ReactErrorBoundary>
    </React.StrictMode>
  )
}

ReactDOM.render(<Root />, document.getElementById('root'))
